import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessagesSquare, Sparkles, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

const PURPOSES = ["Appointment reminder", "Post-op care instructions", "Treatment plan follow-up", "Recall / check-up", "Payment reminder"];

export default function PatientCommunication() {
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/patients").then((r) => setPatients(r.data)); }, []);

  const generate = async () => {
    if (!patientId) return toast.error("Select a patient");
    const p = patients.find((x) => x.id === patientId);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/insight", {
        prompt: `Draft a warm, professional patient message for the purpose: "${purpose}". Keep it under 100 words, friendly and clear. Address the patient by first name.`,
        context: { patient: p.name, chief_complaint: p.chief_complaint },
      });
      setDraft(data.insight);
    } catch { toast.error("AI unavailable"); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Patient Communication" subtitle="AI-drafted patient messages — you review and send" breadcrumb={["Intelligence", "Patient Communication"]} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SurfaceCard className="p-5">
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Patient</Label>
              <Select value={patientId} onValueChange={setPatientId}>
                <SelectTrigger className="mt-1.5" data-testid="comm-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.patient_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-semibold">Purpose</Label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{PURPOSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={generate} disabled={busy} data-testid="comm-generate-btn" className="w-full bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Drafting...</> : <><Sparkles className="h-4 w-4 mr-1.5" />Draft Message</>}
            </Button>
          </div>
        </SurfaceCard>

        <SurfaceCard className="p-5">
          <div className="flex items-center justify-between mb-3"><div className="flex items-center gap-2"><MessagesSquare className="h-4 w-4 text-[#00A85A]" /><span className="text-sm font-bold text-[#101516] dark:text-white">Draft Message</span></div>
            {draft && <button onClick={() => { navigator.clipboard.writeText(draft); toast.success("Copied"); }} className="text-[#8B9698] hover:text-[#00A85A]"><Copy className="h-4 w-4" /></button>}</div>
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={8} placeholder="AI-drafted message will appear here for your review..." data-testid="comm-draft" className="resize-none" />
          <Button onClick={() => draft && toast.success("Message sent to patient")} disabled={!draft} className="w-full mt-3 bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg">Review & Send</Button>
        </SurfaceCard>
      </div>
    </div>
  );
}
